import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useClients } from '../hooks/useClients';
import { useAgents } from '../hooks/useAgents';
import { usePosts } from '../hooks/usePosts';
import { useApprovals } from '../hooks/useApprovals';
import { usePermissions } from '../hooks/usePermissions';
import BrandGuideViewer from '../components/BrandGuideViewer';
import CalendarWidget from '../components/CalendarWidget';
import AgentCard from '../components/AgentCard';
import Modal from '../components/shared/Modal';
import ClientForm from '../components/forms/ClientForm';
import ApprovalPanel from '../components/forms/ApprovalPanel';

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getClient, updateClient, deleteClient, loading, error, refetch } = useClients();
  const { agents } = useAgents();
  const { updatePostStatus } = usePosts({ onUpdate: refetch });
  const { approvals, submitApproval, loadingApprovals } = useApprovals();
  const { isAdmin, isCliente, hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('brand-guide');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        Carregando cliente...
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">&#x26A0;</div>
        <p>Erro: {error}</p>
      </div>
    );
  }

  const client = getClient(id);

  if (!client) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">&#x1F50D;</div>
        <p>Cliente "{id}" nao encontrado</p>
        <Link to="/" style={{ marginTop: 'var(--spacing-md)', display: 'inline-block' }}>
          Voltar ao Overview
        </Link>
      </div>
    );
  }

  const initials = client.name.split(' ').map(w => w[0]).join('').slice(0, 2);

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      await updateClient(id, data);
      setShowEdit(false);
      showToast('success', 'Cliente atualizado');
    } catch (err) {
      showToast('error', `Erro: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteClient(id);
      navigate('/');
    } catch (err) {
      showToast('error', `Erro ao deletar: ${err.message}`);
      setSaving(false);
    }
  };

  const handleApproval = async (postId, action, reviewerName, notes) => {
    try {
      await submitApproval(postId, action, reviewerName, notes);
      if (action === 'approved') {
        await updatePostStatus(postId, 'aprovado');
      } else if (action === 'rejected') {
        await updatePostStatus(postId, 'rejeitado');
      } else if (action === 'revision_requested') {
        await updatePostStatus(postId, 'revisao_solicitada');
      }
      showToast('success', action === 'approved' ? 'Post aprovado' : action === 'rejected' ? 'Post rejeitado' : 'Revisao solicitada');
    } catch (err) {
      showToast('error', `Erro: ${err.message}`);
    }
  };

  const postApprovals = selectedPost
    ? approvals.filter(a => a.postId === selectedPost.id)
    : [];

  return (
    <div>
      {/* Header do cliente */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            <div className="client-avatar" style={{ width: 56, height: 56, fontSize: '1.5rem' }}>{initials}</div>
            <div>
              <h1 className="page-title">{client.name}</h1>
              <p className="page-subtitle">{client.segment}</p>
            </div>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>Editar</button>
              <button className="btn btn-danger" onClick={() => setShowDelete(true)}>Deletar</button>
            </div>
          )}
        </div>
        {client.positioning && (
          <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 'var(--spacing-sm)' }}>
            "{client.positioning}"
          </p>
        )}
        <div className="client-stats" style={{ marginTop: 'var(--spacing-md)' }}>
          <div className="client-stat">
            <div className="client-stat-value">{client.posts.length}</div>
            <div className="client-stat-label">Posts</div>
          </div>
          <div className="client-stat">
            <div className="client-stat-value">{client.channels.join(', ') || '-'}</div>
            <div className="client-stat-label">Canais</div>
          </div>
          <div className="client-stat">
            <div className="client-stat-value">{client.cadence || '-'}</div>
            <div className="client-stat-label">Cadencia</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'brand-guide' ? 'active' : ''}`}
          onClick={() => setActiveTab('brand-guide')}
        >
          Brand Guide
        </button>
        <button
          className={`tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendario ({client.posts.length})
        </button>
        <button
          className={`tab ${activeTab === 'approvals' ? 'active' : ''}`}
          onClick={() => setActiveTab('approvals')}
        >
          Aprovacoes
        </button>
        {!isCliente && (
          <button
            className={`tab ${activeTab === 'agents' ? 'active' : ''}`}
            onClick={() => setActiveTab('agents')}
          >
            Agentes
          </button>
        )}
        {!isCliente && (
          <Link
            to={`/chat?client=${id}`}
            className="tab"
            style={{ textDecoration: 'none' }}
          >
            Chat
          </Link>
        )}
      </div>

      {/* Tab content */}
      {activeTab === 'brand-guide' && (
        <div className="card">
          <BrandGuideViewer content={client.brandGuide} />
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="card">
          <CalendarWidget
            posts={client.posts.filter(p => p.status === 'aprovado' || p.status === 'publicado')}
            onChangeStatus={updatePostStatus}
            onPostClick={(post) => { setSelectedPost(post); setActiveTab('approvals'); }}
          />
        </div>
      )}

      {activeTab === 'approvals' && (
        <div>
          {!selectedPost ? (
            <div>
              <p className="page-subtitle" style={{ marginBottom: 'var(--spacing-lg)' }}>
                Selecione um post para ver/gerenciar aprovacoes
              </p>
              {client.posts.map(post => (
                <div
                  key={post.id}
                  className="calendar-post"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedPost(post)}
                >
                  <div className="calendar-post-info">
                    <div className="calendar-post-title">{post.workingTitle || post.title}</div>
                    <div className="calendar-post-meta">
                      <span>{post.format}</span>
                      <span className={`status-badge status-badge--${post.status}`}>
                        {post.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedPost(null)}
                style={{ marginBottom: 'var(--spacing-md)' }}
              >
                &larr; Voltar aos posts
              </button>
              <ApprovalPanel
                post={selectedPost}
                clientId={id}
                approvals={postApprovals}
                loading={loadingApprovals}
                onApprove={(reviewer, notes) => handleApproval(selectedPost.id, 'approved', reviewer, notes)}
                onReject={(reviewer, notes) => handleApproval(selectedPost.id, 'rejected', reviewer, notes)}
                onRequestRevision={(reviewer, notes) => handleApproval(selectedPost.id, 'revision_requested', reviewer, notes)}
              />
            </div>
          )}
        </div>
      )}

      {activeTab === 'agents' && (
        <div>
          <p className="page-subtitle" style={{ marginBottom: 'var(--spacing-lg)' }}>
            Todos os agentes da agencia trabalham para {client.name}
          </p>
          <div className="grid grid-3">
            {agents.map(agent => (
              <AgentCard key={agent.id} agent={agent} showSquad />
            ))}
          </div>
        </div>
      )}

      {/* Modal editar */}
      {showEdit && (
        <Modal title={`Editar ${client.name}`} onClose={() => setShowEdit(false)}>
          <ClientForm
            client={client}
            onSubmit={handleUpdate}
            onCancel={() => setShowEdit(false)}
            saving={saving}
          />
        </Modal>
      )}

      {/* Modal confirmar delete */}
      {showDelete && (
        <Modal
          title="Confirmar Exclusao"
          onClose={() => setShowDelete(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowDelete(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Deletando...' : 'Sim, Deletar'}
              </button>
            </>
          }
        >
          <p className="confirm-message">
            Tem certeza que deseja deletar <strong>{client.name}</strong>? Esta acao ira remover o cliente e todos os seus posts. Nao pode ser desfeita.
          </p>
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast toast--${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
}

export default ClientDetail;
